import type { PortCallActivity, PortCallLocationType, PortCallStatus } from '@felix/domain';
import { Button, FileButton } from '@mantine/core';
import {
  IconAnchor,
  IconCalendarEvent,
  IconFlag,
  IconInfoCircle,
  IconListCheck,
  IconMapPin,
  IconShip,
  IconUpload,
  IconUser,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { AttachmentCard } from '../pms/AttachmentCard.js';
import { useCountryName } from '../ports/catalog-context.js';
import { formatTzOffset } from '../voyage/noon-report.js';
import { PortCallStatusBadge } from './badges.js';
import classes from './PortCalls.module.css';
import type { PortCallDocument } from './types.js';

/** Kabul edilen belge türleri (K15): JPG, PNG, PDF. */
export const DOCUMENT_ACCEPT = 'application/pdf,image/jpeg,image/png';

/** Yerel saat ve fark: `07.10.2026 08:00 (UTC +03:00)`. */
export function localStamp(iso: string | null, tzOffsetMin: number): string {
  if (!iso) return '—';
  return formatDateTime(iso, tzOffsetMin);
}

export interface SummaryData {
  vesselName: string;
  portName: string;
  portCode: string | null;
  country: string;
  locationType: PortCallLocationType;
  berth: string | null;
  eta: string | null;
  etd: string | null;
  ata: string | null;
  atd: string | null;
  tzOffsetMin: number;
  agentName: string | null;
  activities: readonly PortCallActivity[];
  status: PortCallStatus;
}

/**
 * "Port Call Summary" (ortağın sağ kartı): gemi, liman, ülke, ETA/ETD, acente, rıhtım, faaliyetler
 * ve ETA → ATA → ETD → ATD zaman çizgisi. Formda canlı önizleme, ayrıntıda kayıt.
 */
export function PortCallSummary({ data }: { data: SummaryData }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`portCalls.summary.${key}`);
  const countryName = useCountryName();
  const rows: [ReactNode, string, ReactNode][] = [
    [<IconMapPin key="p" size={15} />, k('port'), data.portName || '—'],
    [<IconFlag key="c" size={15} />, k('country'), data.country ? countryName(data.country) : '—'],
    [
      <IconCalendarEvent key="e" size={15} />,
      k('etaEtd'),
      `${localStamp(data.eta, data.tzOffsetMin)} / ${localStamp(data.etd, data.tzOffsetMin)}`,
    ],
    [<IconUser key="a" size={15} />, k('agent'), data.agentName || '—'],
    [
      <IconAnchor key="b" size={15} />,
      t(`portCalls.locationTypes.${data.locationType}`),
      data.berth || '—',
    ],
  ];
  const steps: [string, string | null, string | null][] = [
    ['eta', data.eta, null],
    ['ata', data.ata, data.ata],
    ['etd', data.etd, null],
    ['atd', data.atd, data.atd],
  ];
  return (
    <Panel
      title={k('title')}
      icon={<IconShip size={20} />}
      extra={<PortCallStatusBadge status={data.status} />}
    >
      <p className={classes.vesselLine}>
        <IconShip size={18} aria-hidden />
        {data.vesselName}
      </p>
      <dl className={classes.summary}>
        {rows.map(([icon, label, value]) => (
          <div key={label} className={classes.contents}>
            <dt>
              <span aria-hidden>{icon}</span>
              {label}
            </dt>
            <dd>{value}</dd>
          </div>
        ))}
        <dt>
          <span aria-hidden>
            <IconListCheck size={15} />
          </span>
          {k('activities')}
        </dt>
        <dd>
          {data.activities.length === 0 ? (
            '—'
          ) : (
            <span className={classes.chips}>
              {data.activities.map((activity) => (
                <span key={activity} className={classes.chip}>
                  {t(`portCalls.activities.${activity}`)}
                </span>
              ))}
            </span>
          )}
        </dd>
      </dl>
      <ol className={classes.timeline} aria-label={k('timeline')}>
        {steps.map(([key, at, done]) => (
          <li key={key} className={classes.step} data-done={done ? true : undefined}>
            <strong>{t(`portCalls.fields.${key}`)}</strong>
            <span>{localStamp(at, data.tzOffsetMin)}</span>
          </li>
        ))}
      </ol>
      <p className={classes.notePad}>
        {k('localTime')} {formatTzOffset(data.tzOffsetMin)}
      </p>
    </Panel>
  );
}

/** Ortağın bilgi kutusu: liman aramaları kayıt altına alınır, ofis görür. */
export function PortCallInfo({ ship }: { ship: boolean }) {
  const { t } = useTranslation();
  return (
    <p className={classes.info}>
      <IconInfoCircle size={18} aria-hidden />
      {t(ship ? 'portCalls.info.ship' : 'portCalls.info.web')}
    </p>
  );
}

/**
 * "Related Documents": belgeler ortak ek altyapısıyla (ADR 0052 §2). Ofiste dosya gemiden geldiyse
 * açılır; gemide "Upload Document".
 */
export function RelatedDocuments({
  documents,
  hrefOf,
  onUpload,
  busy,
}: {
  documents: PortCallDocument[] | null;
  hrefOf?: ((id: string) => string) | undefined;
  onUpload?: ((file: File) => void) | undefined;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`portCalls.documents.${key}`);
  return (
    <Panel
      title={k('title')}
      extra={
        onUpload ? (
          <FileButton onChange={(file) => file && onUpload(file)} accept={DOCUMENT_ACCEPT}>
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                loading={busy ?? false}
                leftSection={<IconUpload size={14} />}
              >
                {k('upload')}
              </Button>
            )}
          </FileButton>
        ) : null
      }
    >
      {documents === null ? (
        <p className={classes.notePad}>{k('loading')}</p>
      ) : documents.length === 0 ? (
        <p className={classes.notePad}>{k('none')}</p>
      ) : (
        <ul className={classes.documents}>
          {documents.map((document) => (
            <AttachmentCard
              key={document.id}
              attachment={document}
              {...(hrefOf ? { href: hrefOf(document.id) } : {})}
            />
          ))}
        </ul>
      )}
      <p className={classes.notePad}>{k('types')}</p>
    </Panel>
  );
}
